/* Cards on the Jobs home: each a count of the work its filters find, and clicking one shows those rows (owner,
 * 2026-10-11: "cards in the Jobs where they filter and set for instance based on status which should show the
 * count and clicking on that card should display that table ... they should be able to add or edit, like we did
 * for customers"). Everyone's cards start with one per waiting state and Overdue; the owner or a pack's managers
 * change those, and anybody adds their own. Counted by the gateway as the person, with the same filters the
 * table then asks for, so the number and the rows always agree.
 *
 * Where a pack asks clients for documents, a last card counts what is still missing, by document and, for one
 * told apart by a number, by that number ("… (account number ending 6789)"), and opens that list.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useToast } from '@jackdesk/shell'
import type { ErpError } from '../api'
import { useErp } from '../context'
import { formatDate } from '../format'
import type {
  CardChange, CategoryTree, MissingDocuments, PacksActive, WorkCard, WorkCards, WorkFilters, WorkScope,
} from './types'

const SCOPES: [WorkScope | '', string][] = [
  ['', 'Whatever the list shows'], ['mine', 'Mine'], ['created', 'Made by me'], ['team', 'My team'],
  ['all', 'Everything'],
]

export function StatCards({ active, scope, picked, onPick, onOpen, refresh }: {
  active: PacksActive
  scope: WorkScope
  picked: WorkCard | null
  onPick: (card: WorkCard | null) => void
  onOpen: (task: string) => void
  /** Changes whenever the list is loaded again, so the counts follow it. */
  refresh: unknown
}) {
  const erp = useErp()
  const [cards, setCards] = useState<WorkCards | null>(null)
  const [missing, setMissing] = useState<MissingDocuments | null>(null)
  const [showMissing, setShowMissing] = useState(false)
  const [editing, setEditing] = useState(false)

  useEffect(() => {
    const controller = new AbortController()
    erp.workCards(scope, controller.signal).then(setCards).catch(() => setCards(null))
    erp.missingDocuments(controller.signal).then(setMissing).catch(() => setMissing(null))
    return () => controller.abort()
  }, [erp, scope, refresh])

  if (!cards) return null
  return (
    <section style={{ display: 'grid', gap: 8 }} aria-label="Cards">
      <div className="work-stats">
        {cards.cards.map((c) => (
          <button key={c.id} type="button" className="work-stat" aria-pressed={picked?.id === c.id}
                  onClick={() => onPick(picked?.id === c.id ? null : c)}
                  title={describe(c.filters, active)}>
            <span className="work-stat-n">{c.count ?? '—'}</span>
            <span className="work-stat-l">{c.label}</span>
          </button>
        ))}
        {missing ? (
          <button type="button" className="work-stat work-stat-warn" aria-pressed={showMissing}
                  onClick={() => setShowMissing(!showMissing)}
                  title="Documents still missing on open work, each named by its number where it has one">
            <span className="work-stat-n">{missing.count}{missing.truncated ? '+' : ''}</span>
            <span className="work-stat-l">Missing documents</span>
          </button>
        ) : null}
        <button type="button" className="btn btn-quiet btn-sm" onClick={() => setEditing(true)}
                style={{ alignSelf: 'center' }}>Edit cards</button>
      </div>
      {showMissing && missing ? <MissingList missing={missing} onOpen={onOpen} /> : null}
      {editing ? (
        <CardEditor active={active} cards={cards} scope={scope}
                    onChanged={(next) => {
                      setCards(next)
                      if (picked && !next.cards.some((c) => c.id === picked.id)) onPick(null)
                      else if (picked) onPick(next.cards.find((c) => c.id === picked.id) ?? null)
                    }}
                    onClose={() => setEditing(false)} />
      ) : null}
    </section>
  )
}

/** A card's filters in words: "With checker · overdue · <category> · mine". */
export function describe(f: WorkFilters, active: PacksActive, groups?: Map<string, string>): string {
  const scope = SCOPES.find(([v]) => v && v === f.scope)?.[1]
  const pack = f.pack ? active.packs.find((p) => p.id === f.pack)?.title ?? f.pack : ''
  return [f.state, f.overdue ? 'overdue' : '', pack, f.group ? groups?.get(f.group) ?? 'a category' : '',
          f.category ?? '', scope?.toLowerCase() ?? '', f.closed ? 'finished included' : '']
    .filter(Boolean).join(' · ') || 'Everything open'
}

function MissingList({ missing, onOpen }: { missing: MissingDocuments; onOpen: (task: string) => void }) {
  const byClient = useMemo(() => {
    const out = new Map<string, MissingDocuments['rows']>()
    for (const r of missing.rows) out.set(r.client_name, [...(out.get(r.client_name) ?? []), r])
    return [...out.entries()]
  }, [missing])
  if (!missing.rows.length) {
    return <div className="card card-pad small muted" role="status">Nothing is missing on open work.</div>
  }
  return (
    <div className="card card-pad" style={{ display: 'grid', gap: 10 }} aria-label="Missing documents">
      <div className="small muted">
        {missing.count} document{missing.count === 1 ? '' : 's'} from {missing.clients} client
        {missing.clients === 1 ? '' : 's'}{missing.truncated ? ' (the first 500)' : ''}. Statements and others told
        apart by a number are named by it, so the one not provided is plain.
      </div>
      {byClient.map(([client, rows]) => (
        <div key={client} style={{ display: 'grid', gap: 4 }}>
          <strong className="small">{client}</strong>
          {rows.map((r) => (
            <div key={r.id} className="row small" style={{ gap: 8, flexWrap: 'wrap' }}>
              <span style={{ minWidth: 220 }}>{r.shown}{r.copy === 'hard' ? ' · paper copy' : r.copy === 'soft' ? ' · file' : ''}</span>
              <button type="button" className="btn btn-quiet btn-sm" onClick={() => onOpen(r.task)}>
                {r.subject}{r.due ? ` · due ${formatDate(r.due)}` : ''}
              </button>
              {r.asked ? <span className="muted">asked {r.asked}×</span> : <span className="muted">not asked yet</span>}
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}

type Draft = { id: number | null; label: string; filters: WorkFilters; everyone: boolean }

function CardEditor({ active, cards, scope, onChanged, onClose }: {
  active: PacksActive; cards: WorkCards; scope: WorkScope
  onChanged: (next: WorkCards) => void; onClose: () => void
}) {
  const erp = useErp()
  const toast = useToast()
  const [draft, setDraft] = useState<Draft | null>(null)
  const [busy, setBusy] = useState(false)
  const [tree, setTree] = useState<CategoryTree | null>(null)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])
  useEffect(() => {
    const controller = new AbortController()
    erp.workCategories(controller.signal).then(setTree).catch(() => setTree(null))
    return () => controller.abort()
  }, [erp])
  const groups = useMemo(() => new Map((tree?.packs ?? []).flatMap((p) => p.tree.map((c) => [String(c.id), c.label]))),
                         [tree])

  const send = useCallback(async (change: CardChange, said: string) => {
    setBusy(true)
    try {
      onChanged(await erp.changeWorkCard(change, scope))
      toast('ok', said)
      return true
    } catch (e) {
      toast('error', (e as ErpError).message)
      return false
    } finally {
      setBusy(false)
    }
  }, [erp, scope, onChanged, toast])

  const mayChange = (c: WorkCard) => c.mine || cards.may_edit
  const save = async () => {
    if (!draft) return
    const ok = draft.id === null
      ? await send({ action: 'add', label: draft.label.trim(), filters: draft.filters, everyone: draft.everyone },
                   'Card added.')
      : await send({ action: 'edit', id: draft.id, label: draft.label.trim(), filters: draft.filters }, 'Card saved.')
    if (ok) setDraft(null)
  }

  return (
    <div className="palette-wrap" role="dialog" aria-modal="true" aria-label="Edit cards"
         onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className="money-dialog" style={{ width: 'min(560px, 94vw)', maxHeight: '86vh', overflow: 'auto' }}>
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <b>Cards</b>
          <button type="button" className="btn btn-quiet btn-sm" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="small muted">
          Each card counts the work its filters find; clicking it shows those rows.
          {cards.may_edit ? ' Cards for everyone are yours to change.' : ' Cards for everyone are set by the owner or the pack’s managers; add your own.'}
        </div>
        <div style={{ display: 'grid', gap: 6 }}>
          {cards.cards.map((c, i) => (
            <div key={c.id} className="small work-card-row">
              <div style={{ minWidth: 0 }}>
                <strong>{c.label}</strong> <span className={`chip ${c.mine ? 'chip-info' : ''}`}>{c.mine ? 'Mine' : 'Everyone'}</span>
                <div className="muted">{describe(c.filters, active, groups)}</div>
              </div>
              {mayChange(c) ? (
                <div className="row" style={{ gap: 2, flexWrap: 'nowrap' }}>
                  <button type="button" className="btn btn-quiet btn-sm" disabled={busy || i === 0}
                          aria-label={`Move ${c.label} left`}
                          onClick={() => void send({ action: 'move', id: c.id, to: 'left' }, 'Moved.')}>←</button>
                  <button type="button" className="btn btn-quiet btn-sm" disabled={busy}
                          aria-label={`Move ${c.label} right`}
                          onClick={() => void send({ action: 'move', id: c.id, to: 'right' }, 'Moved.')}>→</button>
                  <button type="button" className="btn btn-quiet btn-sm" disabled={busy}
                          onClick={() => setDraft({ id: c.id, label: c.label, filters: c.filters, everyone: !c.mine })}>
                    Edit
                  </button>
                  <button type="button" className="btn btn-quiet btn-sm" disabled={busy}
                          onClick={() => void send({ action: 'remove', id: c.id }, `“${c.label}” removed.`)}>
                    Remove
                  </button>
                </div>
              ) : <span />}
            </div>
          ))}
        </div>
        {draft ? (
          <CardForm active={active} tree={tree} draft={draft} setDraft={setDraft} mayEveryone={cards.may_edit}
                    busy={busy} onSave={() => void save()} onCancel={() => setDraft(null)} />
        ) : (
          <div className="row">
            <button type="button" className="btn btn-sm"
                    onClick={() => setDraft({ id: null, label: '', filters: {}, everyone: false })}>+ Add a card</button>
          </div>
        )}
      </div>
    </div>
  )
}

function CardForm({ active, tree, draft, setDraft, mayEveryone, busy, onSave, onCancel }: {
  active: PacksActive; tree: CategoryTree | null; draft: Draft; setDraft: (d: Draft) => void
  mayEveryone: boolean; busy: boolean; onSave: () => void; onCancel: () => void
}) {
  const f = draft.filters
  const set = (change: Partial<WorkFilters>) => {
    const next: WorkFilters = { ...f, ...change }
    for (const k of Object.keys(next) as (keyof WorkFilters)[]) if (!next[k]) delete next[k]
    setDraft({ ...draft, filters: next })
  }
  const withCategories = (tree?.packs ?? []).filter((p) => !f.pack || p.pack === f.pack)
  const only = withCategories.length === 1 ? withCategories[0] : undefined
  // A category (any of its kinds) or one kind of work, as one choice: "g:12" or "c:<the kind's value>".
  const kindValue = f.group ? `g:${f.group}` : f.category ? `c:${f.category}` : ''
  return (
    <form className="card card-pad" style={{ display: 'grid', gap: 10 }} aria-label={draft.id ? 'Change the card' : 'A new card'}
          onSubmit={(e) => { e.preventDefault(); if (draft.label.trim()) onSave() }}>
      <label className="field-row"><span>Name</span>
        <input className="input" value={draft.label} maxLength={60} autoFocus aria-label="Card name"
               onChange={(e) => setDraft({ ...draft, label: e.target.value })} placeholder="e.g. Waiting on sign-off" />
      </label>
      <label className="field-row"><span>Whose</span>
        <select className="select" value={f.scope ?? ''} aria-label="Whose work"
                onChange={(e) => set({ scope: (e.target.value || undefined) as WorkScope | undefined })}>
          {SCOPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </label>
      <label className="field-row"><span>State</span>
        <select className="select" value={f.state ?? ''} aria-label="State" onChange={(e) => set({ state: e.target.value })}>
          <option value="">Any</option>
          {active.core.states.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </label>
      {active.packs.length > 1 ? (
        <label className="field-row"><span>Pack</span>
          <select className="select" value={f.pack ?? ''} aria-label="Pack"
                  onChange={(e) => set({ pack: e.target.value, group: undefined, category: undefined })}>
            <option value="">Any</option>
            {active.packs.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
          </select>
        </label>
      ) : null}
      {only ? (
        <label className="field-row"><span>Kind of work</span>
          <select className="select" value={kindValue} aria-label="Kind of work"
                  onChange={(e) => {
                    const v = e.target.value
                    set({ pack: only.pack, group: v.startsWith('g:') ? v.slice(2) : undefined,
                          category: v.startsWith('c:') ? v.slice(2) : undefined })
                  }}>
            <option value="">Any</option>
            {only.tree.map((c) => (
              <optgroup key={c.id} label={c.label}>
                <option value={`g:${c.id}`}>All of {c.label}</option>
                {c.children.map((s) => <option key={s.id} value={`c:${s.value}`}>{s.label}</option>)}
              </optgroup>
            ))}
          </select>
        </label>
      ) : withCategories.length > 1 ? (
        <div className="small muted">Choose the pack to count one kind of work.</div>
      ) : null}
      <label className="check small">
        <input type="checkbox" checked={Boolean(f.overdue)} onChange={(e) => set({ overdue: e.target.checked ? '1' : undefined })} />
        Only overdue
      </label>
      <label className="check small">
        <input type="checkbox" checked={Boolean(f.closed)} onChange={(e) => set({ closed: e.target.checked ? '1' : undefined })} />
        Count finished work too
      </label>
      {draft.id === null && mayEveryone ? (
        <label className="check small">
          <input type="checkbox" checked={draft.everyone} onChange={(e) => setDraft({ ...draft, everyone: e.target.checked })} />
          Everyone sees it
        </label>
      ) : null}
      <div className="row" style={{ gap: 8 }}>
        <button type="submit" className="btn btn-primary btn-sm" disabled={busy || !draft.label.trim()}>
          {draft.id === null ? 'Add the card' : 'Save'}
        </button>
        <button type="button" className="btn btn-quiet btn-sm" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  )
}
